import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import type * as L from 'leaflet';
import { Subscription } from 'rxjs';
import { TILE_URL, TILE_ATTRIBUTION } from '../map-config';
import { loadLeaflet } from '../leaflet-loader';
import { CATEGORY_META, ListingCatalogService, type Category, type IPrivateListing } from '@cnt-workspace/data-access';

export interface NetworkMapPin {
  lat: number;
  lon: number;
  category: Category;
}

/** localStorage key for the last pin set, so repeat visits show pins at once. */
export const NETWORK_MAP_PINS_KEY = 'cnt-home-map-pins';
const PINS_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Live OSM map preview used in the "Meet the CurbNTurf Community" sections
 * on home and /host. Frames North America and plots only private hosts (no
 * boondocking), each as a round pin in its land-type color and icon from
 * CATEGORY_META, so the section reads as the community of hosts. Pins are
 * non-interactive: the section's CTA opens the real map.
 *
 * Loading is split so tiles never wait on data: Leaflet and the private
 * listings request start as soon as the component is created, the map (and
 * its tiles) builds when the section nears the viewport, and pins draw from
 * the last saved set right away, then from fresh data when it arrives.
 */
@Component({
  selector: 'cnt-network-map',
  standalone: true,
  // Leaflet fills the map div with its own pane/tile tree, which Angular did
  // not render and must not try to match against the server output.
  host: { ngSkipHydration: 'true' },
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div #host class="nm-wrap">
      <div #mapEl class="nm-map"></div>
    </div>
  `,
  styles: [`
    /* Fallback min-height so the map still renders when the parent uses
       auto height. Consumers can still stretch the map with a fixed
       parent height; min-height only kicks in when there's nothing else. */
    :host { display: block; width: 100%; height: 100%; min-height: 320px; }
    @media (min-width: 768px) { :host { min-height: 480px; } }
    .nm-wrap { position: relative; width: 100%; height: 100%; overflow: hidden; background: #f2efe9; }
    .nm-map  { position: absolute; inset: 0; }
    ::ng-deep .nm-map .leaflet-control-container,
    ::ng-deep .nm-map .leaflet-control-attribution { display: none; }
    ::ng-deep .nm-map { cursor: default; }
    ::ng-deep .nm-pin-wrap { background: transparent; border: none; }
    ::ng-deep .nm-pin {
      width: 26px; height: 26px; border-radius: 9999px;
      background: var(--cat); border: 2px solid #fff;
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.28);
      display: flex; align-items: center; justify-content: center; color: #fff;
    }
    ::ng-deep .nm-pin .material-symbols-outlined { font-size: 15px; font-variation-settings: 'FILL' 1; line-height: 1; }
  `],
})
export class NetworkMapComponent implements AfterViewInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private catalog = inject(ListingCatalogService);

  @ViewChild('host', { static: true }) hostEl!: ElementRef<HTMLElement>;
  @ViewChild('mapEl', { static: true }) mapEl!: ElementRef<HTMLElement>;

  private map: L.Map | null = null;
  private leaflet: typeof L | null = null;
  private pinLayer: L.LayerGroup | null = null;
  private freshPins: NetworkMapPin[] | null = null;
  private observer: IntersectionObserver | null = null;
  private listingsSub: Subscription | null = null;
  private mounted = false;
  private destroyed = false;

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    // Warm both before the map is needed.
    const leaflet = loadLeaflet();
    this.listingsSub = this.catalog.getPrivateListings$().subscribe((listings) => {
      this.freshPins = toPins(listings);
      savePins(this.freshPins);
      this.renderPins(this.freshPins);
    });
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting) && !this.mounted) {
        this.mounted = true;
        this.observer?.disconnect();
        void this.mount(leaflet);
      }
    }, { rootMargin: '800px' });
    this.observer.observe(this.hostEl.nativeElement);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.observer?.disconnect();
    this.listingsSub?.unsubscribe();
    this.map?.remove();
  }

  private async mount(leaflet: Promise<typeof L>): Promise<void> {
    const L = await leaflet;
    if (this.destroyed) return;
    this.leaflet = L;
    this.map = L.map(this.mapEl.nativeElement, {
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      dragging: false,
      touchZoom: false,
      boxZoom: false,
      keyboard: false,
      preferCanvas: true,
    });
    L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 18 }).addTo(this.map);

    // Always North America centred on the lower 48 (with southern Canada and
    // northern Mexico around it), whatever the pins' spread. Zoom stays a whole
    // level (fractional zoom leaves tile seams). Wider maps floor the exact fit,
    // as fitBounds would; on a phone-width map flooring drops to half the
    // hemisphere, so round to the nearest level there instead.
    const usBounds = L.latLngBounds([[23, -126], [51, -66]]);
    this.map.options.zoomSnap = 0;
    const exactZoom = this.map.getBoundsZoom(usBounds, false, L.point(8, 8));
    this.map.options.zoomSnap = 1;
    const narrow = this.hostEl.nativeElement.clientWidth < 640;
    this.map.setView(usBounds.getCenter(), narrow ? Math.round(exactZoom) : Math.floor(exactZoom));

    this.pinLayer = L.layerGroup().addTo(this.map);
    this.renderPins(this.freshPins ?? readSavedPins() ?? []);
  }

  /** Replaces the drawn pins. A no-op until the map exists; mount() draws the latest set. */
  private renderPins(pins: NetworkMapPin[]): void {
    const L = this.leaflet;
    if (!L || !this.pinLayer) return;
    this.pinLayer.clearLayers();
    for (const pin of pins) {
      const meta = CATEGORY_META[pin.category as Category];
      if (!meta) continue;
      const icon = L.divIcon({
        className: 'nm-pin-wrap',
        html: `<div class="nm-pin" style="--cat:${meta.color}"><span class="material-symbols-outlined" aria-hidden="true">${meta.icon}</span></div>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
      });
      L.marker([pin.lat, pin.lon], { icon, interactive: false, keyboard: false }).addTo(this.pinLayer);
    }
  }
}

function toPins(listings: IPrivateListing[]): NetworkMapPin[] {
  return listings
    .filter((l) => CATEGORY_META[l.category] && Number.isFinite(l.coordinates?.lat) && Number.isFinite(l.coordinates?.lon))
    .map((l) => ({ lat: l.coordinates.lat, lon: l.coordinates.lon, category: l.category }));
}

function readSavedPins(): NetworkMapPin[] | null {
  try {
    const saved = JSON.parse(localStorage.getItem(NETWORK_MAP_PINS_KEY) ?? 'null');
    if (!saved || !Array.isArray(saved.pins) || Date.now() - saved.ts > PINS_MAX_AGE_MS) return null;
    return saved.pins;
  } catch {
    return null;
  }
}

function savePins(pins: NetworkMapPin[]): void {
  // An empty set usually means the request failed; keep the last good one.
  if (!pins.length) return;
  try {
    localStorage.setItem(NETWORK_MAP_PINS_KEY, JSON.stringify({ ts: Date.now(), pins }));
  } catch {
    /* storage unavailable: pins just load from the network */
  }
}
